import { useTranslation } from 'react-i18next'
import type { AnswerValue, SurveyQuestion } from 'shared'
import { Input, Select, Textarea } from '../ui'
import { cx, focusRing } from '../../lib/utils'
import TableField from './TableField'

interface QuestionFieldProps {
  question: SurveyQuestion
  value: AnswerValue
  onChange: (value: AnswerValue) => void
  error?: string | null
  disabled?: boolean
  /** A table only: show it as text, for an answer that is already in. */
  readOnly?: boolean
}

/**
 * Renders one question of any type. Shared by the member form and the operator
 * preview, so the two can never drift into showing a question differently.
 */
export default function QuestionField({
  question,
  value,
  onChange,
  error,
  disabled,
  readOnly,
}: QuestionFieldProps) {
  const { t } = useTranslation()
  const config = question.config ?? {}
  const fieldId = `q-${question.id}`
  const describedBy = [
    question.description ? `${fieldId}-hint` : null,
    error ? `${fieldId}-error` : null,
  ]
    .filter(Boolean)
    .join(' ')

  // A heading groups the questions under it; there is nothing to answer.
  if (question.type === 'section') {
    return (
      <div className="bg-gray-100 px-6 py-5 dark:bg-gray-800/50" role="group" aria-label={question.label}>
        <h2 className="font-display text-display-sm text-primary-600 dark:text-gray-50">{question.label}</h2>
        {question.description && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{question.description}</p>
        )}
      </div>
    )
  }

  // min-w-0: a fieldset will not shrink below its content otherwise, and a wide
  // table would push the page out instead of scrolling inside its own box.
  return (
    <fieldset className="min-w-0 border-0 p-0">
      <legend
        id={`${fieldId}-label`}
        className="mb-1.5 block text-[17px] font-medium leading-6 text-primary-600 dark:text-gray-50"
      >
        {question.label}
        {question.required && (
          <span className="ml-1 text-red-500" aria-hidden="true">
            *
          </span>
        )}
      </legend>

      {question.description && (
        <p id={`${fieldId}-hint`} className="mb-2 text-sm text-gray-500 dark:text-gray-400">
          {question.description}
        </p>
      )}

      {renderControl()}

      {error && (
        <p
          id={`${fieldId}-error`}
          role="alert"
          className="mt-1.5 text-sm text-red-600 dark:text-red-400"
        >
          {error}
        </p>
      )}
    </fieldset>
  )

  function renderControl() {
    // The visible label is the <legend>, which names the group but not the
    // control inside it — without this the input has no accessible name at all.
    const common = {
      id: fieldId,
      disabled,
      'aria-labelledby': `${fieldId}-label`,
      'aria-describedby': describedBy || undefined,
      'aria-invalid': error ? true : undefined,
    }

    switch (question.type) {
      case 'scale': {
        const min = config.min ?? 1
        const max = config.max ?? 5
        const steps = Array.from({ length: max - min + 1 }, (_, index) => min + index)

        return (
          // Inline block, so the min/max captions end under the first and last
          // step rather than at the far edges of the page.
          <div className="inline-block">
            <div
              role="radiogroup"
              aria-labelledby={`${fieldId}-label`}
              className="mt-1.5 flex flex-wrap gap-2"
            >
              {steps.map((step) => {
                const selected = value === step
                return (
                  <button
                    key={step}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    disabled={disabled}
                    onClick={() => onChange(selected ? null : step)}
                    className={cx(
                      'size-10 rounded-md border text-sm font-medium transition disabled:opacity-50',
                      selected
                        ? 'border-primary-500 bg-primary-500 text-white'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-primary-300 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200',
                      focusRing,
                    )}
                  >
                    {step}
                  </button>
                )
              })}
            </div>
            {(config.minLabel || config.maxLabel) && (
              <div className="mt-1.5 flex justify-between text-xs text-gray-500 dark:text-gray-400">
                <span>{config.minLabel}</span>
                <span>{config.maxLabel}</span>
              </div>
            )}
          </div>
        )
      }

      case 'long_text':
        return (
          <Textarea
            {...common}
            rows={5}
            maxLength={config.maxLength}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => onChange(event.target.value)}
          />
        )

      case 'single_choice':
        return (
          <Select
            {...common}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => onChange(event.target.value || null)}
            options={[
              { value: '', label: t('survey.choosePlaceholder') },
              // Text, whatever the template wrote: the menu returns text.
              ...(config.options ?? []).map((option) => ({ ...option, value: String(option.value) })),
            ]}
          />
        )

      case 'multi_choice': {
        const selected = Array.isArray(value) ? (value as string[]) : []
        return (
          <div role="group" aria-labelledby={`${fieldId}-label`} className="mt-1.5 space-y-2">
            {(config.options ?? []).map((raw) => {
              // Text, whatever the template wrote: that is what is stored and compared.
              const option = { ...raw, value: String(raw.value) }
              return (
                <label
                  key={option.value}
                  className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200"
                >
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={selected.includes(option.value)}
                    onChange={(event) =>
                      onChange(
                        event.target.checked
                          ? [...selected, option.value]
                          : selected.filter((entry) => entry !== option.value),
                      )
                    }
                    className="size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900"
                  />
                  {option.label}
                </label>
              )
            })}
          </div>
        )
      }

      case 'number':
        return (
          <Input
            {...common}
            type="number"
            min={config.min}
            max={config.max}
            step={config.step}
            value={typeof value === 'number' ? String(value) : ''}
            onChange={(event) =>
              onChange(event.target.value === '' ? null : Number(event.target.value))
            }
          />
        )

      case 'boolean':
        return (
          <div role="radiogroup" aria-labelledby={`${fieldId}-label`} className="mt-1.5 flex gap-2">
            {[true, false].map((option) => (
              <button
                key={String(option)}
                type="button"
                role="radio"
                aria-checked={value === option}
                disabled={disabled}
                onClick={() => onChange(value === option ? null : option)}
                className={cx(
                  'rounded-md border px-4 py-2 text-sm font-medium transition disabled:opacity-50',
                  value === option
                    ? 'border-primary-500 bg-primary-500 text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:border-primary-300 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-200',
                  focusRing,
                )}
              >
                {t(option ? 'common.yes' : 'common.no')}
              </button>
            ))}
          </div>
        )

      case 'date':
        return (
          <Input
            {...common}
            type="date"
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => onChange(event.target.value || null)}
          />
        )

      case 'file':
        // Attachments are uploaded separately and referenced by id; the form
        // shows what is attached rather than re-implementing the uploader.
        return (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('survey.fileCount', { count: Array.isArray(value) ? value.length : 0 })}
          </p>
        )

      case 'table':
        return (
          <TableField
            question={question}
            value={value}
            onChange={onChange}
            disabled={disabled}
            readOnly={readOnly}
            showErrors={Boolean(error)}
          />
        )

      case 'short_text':
      default:
        return (
          <Input
            {...common}
            maxLength={config.maxLength}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => onChange(event.target.value)}
          />
        )
    }
  }
}
